"use client"

import { useState, useEffect } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { format } from "date-fns"
import { CalendarDays, CalendarIcon, Check, Zap, FileSpreadsheet, AlertCircle, Settings } from 'lucide-react'
import { toast } from "sonner"
import { API_ENDPOINTS } from "../../../constants/config"
import LateFeeConfigDialog from "./LateFeeConfigDialog"

function MakeLiveFees() {
  const [loading, setLoading] = useState(false)
  const [classes, setClasses] = useState([])
  const [selectedClasses, setSelectedClasses] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [error, setError] = useState("")

  // Store dates for each quarter and class
  const [dates, setDates] = useState({
    q1: {},
    q2: {},
    q3: {},
    q4: {},
  })

  const [liveQuarters, setLiveQuarters] = useState({});
  const [hasChanges, setHasChanges] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [classFeeStructures, setClassFeeStructures] = useState({})
  const [lateFeeDialogOpen, setLateFeeDialogOpen] = useState(false)
  const [selectedLateFeeConfig, setSelectedLateFeeConfig] = useState({ classId: null, className: null, quarter: null });

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true)
        const response = await fetch(API_ENDPOINTS.classes, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
        if (!response.ok) throw new Error("Failed to fetch classes")
        const data = await response.json()
        setClasses(data.data.classes)
      } catch (error) {
        setError("Failed to load classes. Please try again.")
      } finally {
        setLoading(false)
      }
    }
    fetchClasses()
  }, [])

  useEffect(() => {
    const fetchExistingSchedules = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.feesSchedules, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch schedules");

        const data = await response.json();

        if (data.data.schedules) {
          const newDates = {
            q1: {},
            q2: {},
            q3: {},
            q4: {},
          };
          const newLiveQuarters = {};

          Object.entries(data.data.schedules).forEach(([className, quarters]) => {
            const classObj = classes.find(c => c.name === className);
            if (classObj) {
              if (!newLiveQuarters[classObj._id]) {
                newLiveQuarters[classObj._id] = {};
              }
              Object.entries(quarters).forEach(([quarter, data]) => {
                const makeLiveDate = data.makeLiveDate ? new Date(data.makeLiveDate) : null;
                const dueDate = data.dueDate ? new Date(data.dueDate) : null;
                if (!isNaN(makeLiveDate) && !isNaN(dueDate)) {
                  newDates[quarter][classObj._id] = {
                    makeLiveDate,
                    dueDate,
                  };
                  newLiveQuarters[classObj._id][quarter] = data.isLive || false;
                }
              });
            }
          });

          setDates(newDates);
          setLiveQuarters(newLiveQuarters);
        }
      } catch (error) {
        setError("Failed to load existing schedules");
      }
    };

    const checkFeeStructures = async () => {
      try {
        const feeStructurePromises = classes.map(async (cls) => {
          const response = await fetch(API_ENDPOINTS.feesByClass(cls._id, undefined), {
            credentials: "include",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            return { classId: cls._id, hasFees: data.data.fees && data.data.fees.length > 0 };
          }
          return { classId: cls._id, hasFees: false };
        });

        const results = await Promise.all(feeStructurePromises);
        const feeStructureMap = {};
        results.forEach(result => {
          feeStructureMap[result.classId] = result.hasFees;
        });
        setClassFeeStructures(feeStructureMap);
      } catch (error) {
        console.error("Failed to check fee structures:", error);
      }
    };

    if (classes.length > 0) {
      fetchExistingSchedules();
      checkFeeStructures();
    }
  }, [classes]);

  const handleClassToggle = (className) => {
    setSelectedClasses((prev) => {
      if (prev.includes(className)) {
        return prev.filter((c) => c !== className)
      } else {
        return [...prev, className]
      }
    })
  }

  const handleSelectAllToggle = () => {
    if (selectAll) {
      setSelectedClasses([])
    } else {
      setSelectedClasses(classes.map((c) => c.name))
    }
    setSelectAll(!selectAll)
  }

  const handleDateChange = (quarter, classId, type, date) => {
    const isQuarterLive = dates[quarter][classId]?.isLive;
    if (isQuarterLive && type === "makeLiveDate") {
      return;
    }
    
    // Validate date relationships
    const currentData = dates[quarter][classId] || {};
    if (type === "dueDate" && currentData.makeLiveDate && date) {
      // If setting due date, ensure it's after make live date
      const makeLiveDate = new Date(currentData.makeLiveDate);
      const dueDate = new Date(date);
      // Reset time to compare only dates
      makeLiveDate.setHours(0, 0, 0, 0);
      dueDate.setHours(0, 0, 0, 0);
      
      if (dueDate <= makeLiveDate) {
        setError("Due Date must be after Make Live Date");
        toast.error("Due Date must be after Make Live Date");
        return;
      }
    } else if (type === "makeLiveDate" && currentData.dueDate && date) {
      // If setting make live date, ensure due date is after it
      const makeLiveDate = new Date(date);
      const dueDate = new Date(currentData.dueDate);
      // Reset time to compare only dates
      makeLiveDate.setHours(0, 0, 0, 0);
      dueDate.setHours(0, 0, 0, 0);
      
      if (dueDate <= makeLiveDate) {
        setError("Due Date must be after Make Live Date");
        toast.error("Due Date must be after Make Live Date");
        return;
      }
    }
    
    const classObj = classes.find(c => c._id === classId);
    if (classObj && !selectedClasses.includes(classObj.name)) {
      setSelectedClasses(prev => [...prev, classObj.name]);
    }
    setDates((prev) => ({
      ...prev,
      [quarter]: {
        ...prev[quarter],
        [classId]: {
          ...prev[quarter][classId],
          [type]: date,
        },
      },
    }));
    setHasChanges(prev => ({
      ...prev,
      [`${classId}-${quarter}`]: true
    }));
    setError(""); // Clear error on successful date change
  }

  const validateDates = (classNames, quarter) => {
    if (classNames.length === 0) {
      setError("Please select at least one class")
      return false
    }
    const hasIncompleteDates = classNames.some((className) => {
      const classObj = classes.find((c) => c.name === className)
      if (!classObj) return true
      const classData = dates[quarter][classObj._id]
      const hasOne = classData?.makeLiveDate || classData?.dueDate
      const hasBoth = classData?.makeLiveDate && classData?.dueDate
      return hasOne && !hasBoth
    })
    if (hasIncompleteDates) {
      setError("Both Make Live Date and Due Date are required")
      return false
    }
    
    // Validate that due date is after make live date
    const hasInvalidDateOrder = classNames.some((className) => {
      const classObj = classes.find((c) => c.name === className)
      if (!classObj) return false
      const classData = dates[quarter][classObj._id]
      if (classData?.makeLiveDate && classData?.dueDate) {
        const makeLiveDate = new Date(classData.makeLiveDate)
        const dueDate = new Date(classData.dueDate)
        // Reset time to compare only dates
        makeLiveDate.setHours(0, 0, 0, 0)
        dueDate.setHours(0, 0, 0, 0)
        return dueDate <= makeLiveDate
      }
      return false
    })
    if (hasInvalidDateOrder) {
      setError("Due Date must be after Make Live Date")
      return false
    }
    
    setError("")
    return true
  }

  const handleMakeLiveClick = (className, quarter) => {
    setPendingAction({ className, quarter })
    setShowConfirmDialog(true)
  }

  const handleConfirmMakeLive = async () => {
    if (!pendingAction) return
    const { className, quarter } = pendingAction
    setShowConfirmDialog(false)
    try {
      if (!validateDates([className], quarter)) {
        return;
      }
      setLoading(true);
      const classObj = classes.find((c) => c.name === className);
      
      // Check if fee structure exists for this class
      const feeResponse = await fetch(API_ENDPOINTS.feesByClass(classObj._id, undefined), {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      if (!feeResponse.ok) {
        throw new Error("Failed to check fee structure");
      }
      
      const feeData = await feeResponse.json();
      if (!feeData.data.fees || feeData.data.fees.length === 0) {
        throw new Error("No fee structure found for this class. Please create fee structure first before making fees live.");
      }
      
      const formattedData = [{
        className,
        quarters: {
          [quarter]: dates[quarter][classObj._id]
        }
      }];
      const response = await fetch(API_ENDPOINTS.feesMakeLive, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify({ classes: formattedData }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to make fees live");
      }
      toast.success(`${quarter.toUpperCase()} fees are now live for ${className}!`);
      setSelectedClasses(prev => prev.filter(c => c !== className));
    } catch (error) {
      setError(error.message || "Failed to make fees live")
      toast.error(error.message || "Failed to make fees live")
    } finally {
      setLoading(false)
      setPendingAction(null)
    }
  }

  // Helper function to render date fields
  const renderDateFields = (quarter, classId) => {
    const classObj = classes.find(c => c._id === classId);
    const classData = dates[quarter][classId] || {};
    const isQuarterLive = classData.isLive;
    const isUpdating = loading;
    const hasFeeStructure = classFeeStructures[classId];

    return (
      <div className="bg-gradient-to-br from-[#eaf3fb] to-[#f8fbff] p-4 rounded-lg text-center h-full shadow-sm border border-[#0263cc]/10">
        {!hasFeeStructure && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
            <AlertCircle className="inline mr-1" size={12} />
            No fee structure found
          </div>
        )}
        {isQuarterLive ? (
          <div className="text-sm text-[#0263cc] font-semibold w-full text-left mb-2 flex items-center opacity-60">
            <CalendarDays className="mr-2 text-[#0263cc]" size={16} />
            Make Live Date
            <div className="ml-auto text-xs p-1 rounded bg-[#0263cc]/10 text-[#0263cc]">
              {classData.makeLiveDate && !isNaN(classData.makeLiveDate)
                ? format(classData.makeLiveDate, "PP")
                : "Select"}
            </div>
          </div>
        ) : (
          <Popover>
            <PopoverTrigger asChild disabled={isQuarterLive}>
              <button className="text-sm text-[#0263cc] font-semibold w-full text-left mb-2 flex items-center">
                <CalendarDays className="mr-2 text-[#0263cc]" size={16} />
                Make Live Date
                <div className={`ml-auto text-xs p-1 rounded ${
                  classData.makeLiveDate ? "bg-[#0263cc]/10 text-[#0263cc]" : "bg-gray-100"
                }`}>
                  {classData.makeLiveDate && !isNaN(classData.makeLiveDate)
                    ? format(classData.makeLiveDate, "PP")
                    : "Select"}
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={classData.makeLiveDate}
                onSelect={(date) => handleDateChange(quarter, classId, "makeLiveDate", date)}
                disabled={isQuarterLive}
              />
            </PopoverContent>
          </Popover>
        )}

        <div className="mt-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-sm text-[#0263cc] font-semibold w-full text-left flex items-center">
                <CalendarIcon className="mr-2 text-[#0263cc]" size={16} />
                Due Date
                <div className={`ml-auto text-xs p-1 rounded ${
                  classData.dueDate ? "bg-[#0263cc]/10 text-[#0263cc]" : "bg-gray-100"
                }`}>
                  {classData.dueDate && !isNaN(classData.dueDate)
                    ? format(classData.dueDate, "PP")
                    : "Select"}
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={classData.dueDate}
                onSelect={(date) => handleDateChange(quarter, classId, "dueDate", date)}
                disabled={(date) => {
                  // Disable dates that are on or before make live date
                  if (classData.makeLiveDate) {
                    const makeLiveDate = new Date(classData.makeLiveDate);
                    makeLiveDate.setHours(0, 0, 0, 0);
                    const checkDate = new Date(date);
                    checkDate.setHours(0, 0, 0, 0);
                    return checkDate <= makeLiveDate;
                  }
                  return false;
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
        
        {isQuarterLive && (
          <div className="mt-2 text-xs text-[#0263cc] bg-[#eaf3fb] p-2 rounded">
            Make Live Date cannot be modified
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <Button
            onClick={() => {
              setSelectedLateFeeConfig({ 
                classId: classId, 
                className: classObj.name, 
                quarter: quarter 
              })
              setLateFeeDialogOpen(true)
            }}
            disabled={isQuarterLive}
            className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center justify-center rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            variant="outline"
            title={isQuarterLive ? "Cannot update late fees after fees have been made live" : "Configure late fees"}
          >
            <Settings className="mr-1" size={14} />
            Late Fee
          </Button>
          <Button
            onClick={() => handleMakeLiveClick(classObj.name, quarter)}
            className="flex-1 bg-[#0263cc] text-white hover:bg-[#024b99] flex items-center justify-center rounded-lg"
            disabled={
              isUpdating || 
              !dates[quarter][classId]?.dueDate || 
              (!dates[quarter][classId]?.makeLiveDate && !classData.isLive) ||
              !hasChanges[`${classId}-${quarter}`] ||
              !hasFeeStructure
            }
          >
            <Zap className="mr-1" size={14} />
            {isUpdating ? "Updating..." : !hasFeeStructure ? "No Fee" : "Update"}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 bg-white p-8 rounded-2xl shadow-lg mx-auto border border-[#0263cc]/10">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 flex items-start rounded-lg">
          <AlertCircle className="text-red-500 mr-2 mt-0.5" size={18} />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-2xl font-bold mb-2 text-[#0263cc] flex items-center gap-2">
          <FileSpreadsheet className="text-[#0263cc]" size={22} />
          Make Fees Live
        </h3>
        <p className="text-sm text-gray-600">
          Set the dates when fees should become visible to students and when they are due.
          This will make the fees visible on student dashboards.
        </p>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr className="bg-[#0263cc]/10">
              <th className="border border-[#0263cc]/10 p-3 text-left w-20 min-w-[80px]">
                <div className="flex items-center">
                  <FileSpreadsheet className="mr-2 text-[#0263cc]" size={16} />
                  Class
                </div>
              </th>
              <th className="border border-[#0263cc]/10 p-3 text-center w-40">Q1</th>
              <th className="border border-[#0263cc]/10 p-3 text-center w-40">Q2</th>
              <th className="border border-[#0263cc]/10 p-3 text-center w-40">Q3</th>
              <th className="border border-[#0263cc]/10 p-3 text-center w-40">Q4</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center p-4 text-gray-500">
                  Loading classes...
                </td>
              </tr>
            ) : classes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center p-4 text-gray-500">
                  No classes found
                </td>
              </tr>
            ) : (
              classes.map((cls, index) => (
                <tr key={cls._id} className="border-b border-[#0263cc]/10">
                  <td className="border border-[#0263cc]/10 p-3 w-20">
                    <div
                      className={`relative p-2 rounded-lg cursor-pointer text-center transition-all duration-300 ease-in-out flex items-center justify-center ${selectedClasses.includes(cls.name)
                          ? "bg-[#0263cc] text-white shadow-md"
                          : "bg-white border border-[#0263cc] text-[#0263cc] hover:bg-[#0263cc]/10 hover:shadow-sm"
                        }`}
                      onClick={() => handleClassToggle(cls.name)}
                    >
                      {selectedClasses.includes(cls.name)
                        ? (
                          <div className="flex items-center">
                            <Check size={16} className="mr-1" />
                            <span className="text-xs truncate max-w-[60px]">{cls.name}</span>
                          </div>
                        ) : (
                          <span>{index + 1}</span>
                        )}
                    </div>
                  </td>
                  <td className="border border-[#0263cc]/10 p-2">{renderDateFields("q1", cls._id)}</td>
                  <td className="border border-[#0263cc]/10 p-2">{renderDateFields("q2", cls._id)}</td>
                  <td className="border border-[#0263cc]/10 p-2">{renderDateFields("q3", cls._id)}</td>
                  <td className="border border-[#0263cc]/10 p-2">{renderDateFields("q4", cls._id)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0263cc]">Confirm Fee Update</AlertDialogTitle>
            <AlertDialogDescription>
              Please confirm that you have verified:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Class fees are set correctly</li>
                <li>Bus fees are configured</li>
                <li>Late fee rules are defined</li>
                <li>Scholarship criteria are updated</li>
              </ul>
              <span className="block mt-2 text-[#0263cc] font-medium">This action will make the fees visible to students.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#0263cc] hover:bg-[#024b99] text-white" onClick={handleConfirmMakeLive}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LateFeeConfigDialog
        classId={selectedLateFeeConfig.classId}
        className={selectedLateFeeConfig.className}
        quarter={selectedLateFeeConfig.quarter}
        open={lateFeeDialogOpen}
        onOpenChange={setLateFeeDialogOpen}
        onSave={() => {
          toast.success("Late fee configuration saved")
        }}
      />
    </div>
  )
}

export default MakeLiveFees
